/*
 * Shared phone driver (#128) route jobs on the slot settings page: phase labels, plain-language texts for the server's
 * codes, and the few decisions the page makes about a job (server PhoneDriverTransitions, shared recovery contract of
 * 2026-10-17, Server 53aaf84).
 *
 * A job is phone-wide: REQUESTED -> CLAIMED -> STOPPING -> STOPPED -> PROVISIONING -> VERIFYING -> COMMITTED ->
 * RESUMING -> SUCCEEDED. FAILED and EXPIRED keep the hold until a retry, a cancel or a new #128 save supersedes them;
 * CANCELLED (new) releases it with the effective route unchanged. SUCCEEDED and CANCELLED are final (held=false).
 *
 *   cancel     POST /phone-driver/transitions/{op}/cancel: an unclaimed REQUESTED job, or FAILED / EXPIRED that never
 *              reached PROVISIONING (job view cancelAllowed). Otherwise 409 PHONE_TRANSITION_NEEDS_VERIFICATION.
 *              MF-1 (review round 2, Server ba21de4): verificationRequired=true means this job replaced an operation that
 *              already changed the phone; it is never cancellable (the page offers no Cancel even if a view says
 *              otherwise) and only then may the page NOT say "nothing on the phone was changed".
 *   supersede  a #128 save in EITHER direction while the job is FAILED / EXPIRED (supersedeAllowed) starts a new
 *              verified job; saving the route the phone already runs is a VERIFY job (verifyOnly) or closes the hold
 *              (CANCELLED) when nothing on the phone was touched.
 *   retry      the same route again, when retryAllowed.
 *
 * Logs: one console line per decision, tag [phone-transition] (op id, phase, code). The page never sees a claim token.
 */

export const PHASE_LABELS = {
  REQUESTED: 'Waiting for the desktop client',
  CLAIMED: 'Preparing the switch',
  STOPPING: 'Stopping this phone’s workers',
  STOPPED: 'Workers stopped',
  PROVISIONING: 'Setting up the phone connection',
  VERIFYING: 'Checking the new connection',
  COMMITTED: 'Connection verified; restarting previous workers',
  RESUMING: 'Restarting previous workers',
  SUCCEEDED: 'Phone driver switched',
  FAILED: 'The switch failed. Automatic restarts are blocked.',
  EXPIRED: 'The desktop stopped responding. Automatic restarts are blocked.',
  CANCELLED: 'Phone driver switch cancelled. The current connection stays in effect.',
};

/*
 * Every code the server or the desktop can put on a job (errorCode) or answer a dashboard call with (409 / 404).
 * One sentence each, saying what to do next.
 */
export const TRANSITION_ERROR_TEXT = {
  // the desktop's fixed setup failures (BM PhoneRouteCoordinator.failureCode)
  ROOT_PERMISSION_REQUIRED: 'Allow root access for Shell on the phone, then retry safely.',
  MAGISK_BOOT_REQUIRED: 'This phone needs a supported Magisk boot setup before it can switch connections.',
  PHONE_IDENTITY_MISMATCH: 'The phone does not match its assigned serial. Check the device assignment before retrying.',
  PHONE_ALREADY_IN_USE: 'Another desktop or operation is using this phone. Stop it and wait for a fresh stopped report.',
  PHONE_PAIRING_INVALID: 'This desktop could not verify its private phone pairing. Repair the pairing on this desktop before retrying.',
  ADB_CONNECTION_FAILED: 'The desktop could not reconnect to ADB. Check the phone connection before retrying.',
  PHONE_HOST_UNAVAILABLE: 'The phone connection service is unavailable. Check the phone and desktop connection.',
  PHONE_RUNTIME_UNSUPPORTED: 'The phone connection software is incompatible. Install the coordinated supported builds.',
  PHONE_ROUTE_OUTCOME_UNKNOWN: 'The connection change could not be confirmed. Keep workers stopped and check the phone before retrying.',
  PHONE_RECOVERY_PENDING: 'The previous switch still needs recovery. Keep workers stopped until its outcome is confirmed.',
  PHONE_TRANSPORT_VERIFICATION_FAILED: 'The new phone connection did not pass verification. Check the phone before retrying.',
  PHONE_ROUTE_TRANSITION_FAILED: 'The desktop could not finish the switch safely. Check the phone and desktop before trying again.',
  LEASE_EXPIRED: 'The desktop stopped reporting progress before the switch finished (it was closed, asleep or offline).',
  // the server's answers to request / retry / cancel / a #128 save
  PHONE_TRANSITION_NEEDS_VERIFICATION: 'This phone may already have been changed, by this switch or an earlier one, so the switch cannot just be cancelled. Save setting #128 again (ON or OFF) to start a verified switch.',
  EXPLICIT_STOPPED_RECOVERY_REQUIRED: 'This switch cannot simply be retried because the phone was already being changed. Save setting #128 again (ON or OFF) to start a verified switch.',
  PHONE_DEVICE_IDS_DISAGREE: 'A slot on this phone points at two different phones (its device setting #7 and its account disagree). Save that slot’s device (#7) again so both match, then try again.',
  SWITCH_OFF_BEFORE_CHANGING_PHONE_SERIAL: 'Turn the shared phone driver (#128) off for the slots on this phone before changing or removing its serial.',
  SWITCH_OFF_BEFORE_MOVING_SHARED_PHONE: 'Turn the shared phone driver (#128) off before moving this slot to another phone.',
  TRANSITION_ALREADY_FINAL: 'This switch has already finished. Reload the page to see the phone’s current connection.',
  PHONE_TRANSITION_HELD: 'Another phone driver switch is still running on this phone. Wait for it to finish, or cancel it, before asking for another.',
  PHONE_DRIVER_TRANSITION_HOLD: 'This phone is in the middle of a phone driver switch. Wait for it to finish before changing the phone.',
  PHONE_ASSIGNMENT_HELD: 'This phone is in the middle of a phone driver switch; slots cannot move on or off it until it finishes.',
  WAIT_FOR_STOPPED_INVENTORY: 'Waiting for the desktop to report that this phone’s workers are stopped. Try again in a minute.',
  WORKERS_NOT_DURABLY_STOPPED: 'This phone’s workers are still running or set to restart. Stop them on the desktop, then try again.',
  MANAGED_DESKTOP_INVENTORY_REQUIRED: 'Every desktop paired with your account must be online with the updated client. Open the desktop client(s), then try again.',
  UPDATED_DESKTOP_REQUIRED: 'Install the updated desktop client before switching the phone driver.',
  PHONE_NOT_AVAILABLE_ON_MANAGED_DESKTOP: 'No connected desktop reports this phone. Connect it to a desktop running the updated client, then try again.',
  MULTIPLE_RUNNING_DESKTOP_OWNERS: 'More than one desktop is running workers for this phone. Stop them on all but one desktop, then try again.',
  AMBIGUOUS_WORKER_INVENTORY: 'A desktop reported a worker without its phone. Restart the desktop client, then try again.',
  BROWSER_ROUTE_RESERVED: 'A slot on this phone is reserved for browser automation. Release it before switching the phone driver.',
  FOREIGN_PHONE_ALIAS: 'This phone’s serial is also registered under another account. Remove it there first.',
  PHONE_NOT_FOUND: 'This slot’s phone was not found. Check its device assignment (#7).',
  PHONE_HAS_NO_SLOTS: 'This phone has no slots to switch.',
  TRANSITION_NOT_FOUND: 'This phone driver switch no longer exists. Reload the page.',
  SIGN_IN_REQUIRED: 'Your session ended. Sign in again.',
};

export const GENERIC_JOB_ERROR = 'The desktop could not finish safely. Check the phone and desktop before retrying.';

const CODE = /^[A-Z][A-Z0-9_]{2,79}$/;
// Spring's default error body (this server sets include-stacktrace=always but not include-message, so `message` is
// ""): the code is the quoted reason of the ResponseStatusException at the head of `trace`.
const TRACE_CODE = /ResponseStatusException: \d{3}[^"\r\n]*"([A-Z][A-Z0-9_]{2,79})"/;

/** The server's code for a refused call (axios error), or null. */
export function transitionErrorCode(error) {
  const data = error && error.response && error.response.data;
  if (!data) return null;
  if (typeof data === 'string') {
    const t = data.trim();
    if (CODE.test(t)) return t;
    const m = TRACE_CODE.exec(data);
    return m ? m[1] : null;
  }
  if (typeof data !== 'object') return null;
  for (const key of ['code', 'errorCode', 'error', 'message', 'reason']) {
    const v = data[key];
    if (typeof v === 'string' && CODE.test(v.trim())) return v.trim();
  }
  for (const key of ['trace', 'exception']) {
    const v = data[key];
    const m = typeof v === 'string' ? TRACE_CODE.exec(v) : null;
    if (m) return m[1];
  }
  return null;
}

/** Plain sentence for a code, or null when the code is unknown. */
export const transitionErrorText = (code) => (code && TRANSITION_ERROR_TEXT[code]) || null;

/** One console line per page decision about a route job. Fields: op, phase, code, extra. Never a token. */
export function logTransition(event, fields = {}) {
  const parts = [`[phone-transition] ${event}`];
  ['op', 'phase', 'requestedShared', 'effectiveShared', 'code', 'detail'].forEach((k) => {
    if (fields[k] !== undefined && fields[k] !== null) parts.push(`${k}=${fields[k]}`);
  });
  // eslint-disable-next-line no-console
  console.info(parts.join(' '));
}

/** Job view -> the fields logTransition prints. */
export const viewFields = (t, extra = {}) => ({
  op: t && t.operationId, phase: t && t.phase, requestedShared: t && t.requestedShared,
  effectiveShared: t && t.effectiveShared, ...extra,
});

/**
 * The toast line for a refused call: the plain sentence with the code in brackets, or `fallback` for a message that is
 * not one of the codes (e.g. a settings validation sentence). Logs the mapping once - for a refusal without a code only
 * when logUnknown (a transition call; a settings save's own validation message is not a route event).
 */
export function refusalMessage(error, fallback, event = 'refused', { logUnknown = true } = {}) {
  const code = transitionErrorCode(error);
  const text = transitionErrorText(code);
  if (code || logUnknown) logTransition(event, { code: code || 'none', detail: `status=${error && error.response ? error.response.status : 'none'}` });
  if (text) return `${text} (${code})`;
  return fallback;
}

export const routeName = (shared) => (shared ? 'Shared phone driver' : 'ADB');

/** FAILED / EXPIRED and still held: a #128 save in either direction starts a new verified job (recovery contract d). */
export function isSupersedable(t) {
  if (!t || !t.held) return false;
  if (typeof t.supersedeAllowed === 'boolean') return t.supersedeAllowed;
  return t.phase === 'FAILED' || t.phase === 'EXPIRED';
}

/** MF-1: this job (or an operation it replaced) may already have changed the phone; only a verified job releases it. */
export const needsVerification = (t) => !!t && t.verificationRequired === true;

/** Cancel is offered only where the server allows it AND the phone is not waiting for verification. */
export const canCancel = (t) => !!t && !!t.cancelAllowed && !needsVerification(t);

/** A job that is still running: #128 shows the requested route and cannot be edited until it ends. */
export const isRouteLocked = (t) => !!t && !!t.held && !isSupersedable(t);

/** Poll a running job every 2 s; a FAILED / EXPIRED hold changes rarely (a late desktop report): every 10 s. */
export function transitionPollMs(t) {
  if (!t || !t.held) return false;
  return isSupersedable(t) ? 10000 : 2000;
}

/*
 * Review round 2: a hold closed (CANCELLED) after the desktop stopped this phone's workers leaves those slots stopped -
 * nothing restarts them (the desktop only releases its local hold). restartSlotNames is what the desktop stopped.
 */
export function stoppedSlotsText(t) {
  const names = t && t.phase === 'CANCELLED' && Array.isArray(t.restartSlotNames) ? t.restartSlotNames.filter(Boolean) : [];
  if (!names.length) return '';
  return `The slots stopped for the switch (${names.join(', ')}) stay stopped: press Start for them in the desktop client.`;
}

const withStoppedSlots = (sentence, t) => {
  const stopped = stoppedSlotsText(t);
  return stopped ? `${sentence} ${stopped}` : sentence;
};

/** The info toast after Cancel. */
export const cancelNotice = (t) => withStoppedSlots(`Phone driver switch cancelled. The phone stays on ${routeName(t && t.effectiveShared)}.`, t);

/** The info toast after a #128 save that answered with a job (request, supersede, verify or a closed hold). */
export function routeSaveNotice(t) {
  if (!t) return null;
  if (t.phase === 'CANCELLED') return withStoppedSlots(`The phone driver switch was closed. The phone stays on ${routeName(t.effectiveShared)}.`, t);
  if (t.verifyOnly) return `Re-checking this phone’s ${routeName(t.requestedShared)} connection. Its slots restart after the desktop verifies it.`;
  return 'Phone driver switch requested. The current connection stays in effect until verification completes.';
}
